import { formatPrice } from '../../../../shared/js/formatters'
import ToggableDt from './ToggableDt'
import React from 'react'
import type { VariantProps, VariantsProps } from '../../shared/Module'
import type { ChangeEvent, ChangeEventHandler, ReactNode } from 'react'

interface VariantsComponentProps extends VariantsProps {
  onChange: Function
  selectedId?: number
  guideLink: ReactNode
  isChain: boolean
}

interface VariantsWithIndex extends VariantsProps {
  index?: number
  value?: string
}

const Variants = ({
  displayWeight = false,
  guideLink,
  headings,
  onChange,
  selectedId,
  variants,
  isChain,
}: VariantsComponentProps) => {
  const groupedVariants = isChain
    ? groupVariants({ headings, variants })
    : variants
  const selectedVariant = variants.find(({ id }) => id === selectedId)

  return (
    <>
      <GroupedVariant
        group={groupedVariants}
        headings={headings}
        displayWeight={displayWeight}
        handleChange={handleChange}
        handleVariantSelect={handleVariantSelect}
        selectedVariant={selectedVariant}
        guideLink={guideLink}
        isChain={isChain}
      />
    </>
  )

  // Set selected variant
  function handleChange({ target: { value } }: ChangeEvent<HTMLInputElement>) {
    const variantId = Number(value)
    processChange(variantId)
  }

  function processChange(variantId: number) {
    const updatedVariants = variants.map((variant) => ({
      ...variant,
      selected: variant.id === variantId,
    }))
    onChange(updatedVariants)
  }

  // Select first variant in list. Dig in sub-objects when needed
  function handleVariantSelect(value: Array<any> | VariantProps[]) {
    if (Array.isArray(value)) {
      return processChange(value[0].id)
    }
    handleVariantSelect(Object.values(value))
  }
}

export default Variants

function GroupedVariant({
  headings,
  index = 0,
  group,
  guideLink,
  displayWeight,
  handleChange,
  handleVariantSelect,
  selectedVariant,
  isChain,
}: {
  headings: string[]
  index?: number
  group: { [key: string]: any }
  displayWeight: boolean
  handleChange: Function
  handleVariantSelect: Function
  selectedVariant?: VariantProps
  guideLink?: ReactNode
  isChain: boolean
}) {
  if (Array.isArray(group)) {
    return (
      <>
        <dt className={`active${isChain ? '' : ' '}`} aria-expanded>
          {isChain && headings[index]}

          {guideLink}
        </dt>
        <dd>
          <div className={`options-table${isChain ? ' chain' : ''}`}>
            <table>
              <thead>
                <tr>
                  {isChain ? (
                    <th>{headings[index]}</th>
                  ) : (
                    headings.map((heading, hIndex) => (
                      <th key={`heading-${hIndex}`}>{heading}</th>
                    ))
                  )}
                  {displayWeight && <th>Poids</th>}
                  <th>Prix</th>
                  <th>Délai de livraison</th>
                </tr>
              </thead>
              <tbody>
                {group.map((variant) => (
                  <Variant
                    displayWeight={displayWeight}
                    {...variant}
                    key={variant.id}
                    onChange={handleChange}
                    selected={selectedVariant?.id === variant.id}
                    isChain={isChain}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </dd>
      </>
    )
  }

  const selectedLabel = selectedVariant?.labels?.[index]
  const headingId = `variant-title-${index}`
  const values = Object.entries(group)
  const isSize = values.every(([label]: any) => label.match(/[cm]m$/i))
  const sizeKind = isSize ? values[0][0].match(/[cm]m$/i) : undefined

  return (
    <>
      <ToggableDt expanded>
        {headings[index]}
        {sizeKind ? ` (${sizeKind})` : ''}
      </ToggableDt>
      <dd>
        <div className='hidden-input-filter'>
          <ul className='size-filters chain'>
            {values.map(([label, value]: any) => (
              <li key={`${headingId}-${label}`}>
                <input
                  type='radio'
                  id={`${headingId}-${label}`}
                  name={headingId}
                  aria-describedby={headingId}
                  onChange={() => handleVariantSelect(value)}
                  defaultChecked={selectedLabel === label}
                />
                <label htmlFor={`${headingId}-${label}`}>
                  {sizeKind ? label.replace(sizeKind, '') : label}
                </label>
              </li>
            ))}
          </ul>
        </div>
      </dd>
      {selectedLabel && group[selectedLabel] && (
        <GroupedVariant
          group={group[selectedLabel]}
          displayWeight={displayWeight}
          headings={headings}
          index={index + 1}
          handleChange={handleChange}
          handleVariantSelect={handleVariantSelect}
          selectedVariant={selectedVariant}
          isChain={isChain}
        />
      )}
    </>
  )
}

// -------------------------------------------------------------------------

interface VariantComponentProps extends VariantProps {
  onChange: ChangeEventHandler<HTMLInputElement>
  isChain: boolean
}

// Sub component
const Variant = ({
  cents,
  displayWeight,
  id,
  labels,
  onChange,
  quantity,
  regularCents,
  selected,
  shipping: { label: shippingLabel, highlight: shippingHighlight },
  weight,
  isChain,
}: VariantComponentProps) => {
  const shippingSubTree = (
    <>
      <span>{shippingLabel}</span>
      {/* {quantity > 0 && quantity < 4 && (
        <>
          &nbsp;
          <small className='u-hidden@um'>{`(${quantity} en stock)`}</small>
        </>
      )} */}
    </>
  )
  // Use last label for chain since other labels are used as separate "accordions"
  const label = labels[isChain ? labels.length - 1 : 0]

  return (
    <tr>
      <td key={label}>
        <span>
          <input
            checked={selected}
            id={`variant-radio-${id}`}
            onChange={onChange}
            type='radio'
            value={id}
          />
          <label htmlFor={`variant-radio-${id}`}>{label}</label>
        </span>
      </td>

      {!isChain &&
        labels
          .slice(1)
          .map((label, index) => <td key={`label-${index}`}>{label}</td>)}

      {displayWeight && <td>{formatWeight(weight)}</td>}
      <td>
        {regularCents ? (
          <>
            <ins>{formatPrice(cents)}</ins>
            <del>{formatPrice(regularCents)}</del>
          </>
        ) : (
          formatPrice(cents)
        )}
      </td>
      <td>
        {shippingHighlight ? (
          <strong>{shippingSubTree}</strong>
        ) : (
          shippingSubTree
        )}
      </td>
    </tr>
  )
}
const weightFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'decimal',
  // unit: 'gram',
  // unitDisplay: 'narrow',
  maximumFractionDigits: 2,
})

function formatWeight(grams: number) {
  return weightFormatter.format(grams) + ' g'
}

// Because UX expect to split variants by labels for display
// we must group variants by labels and values
export function groupVariants({
  headings,
  variants,
  index = 0,
}: VariantsWithIndex): any {
  if (index === headings.length - 1) {
    // Leaf level
    return variants
  } else {
    // Group by current heading
    const subGrouped: { [key: string]: VariantProps[] } = {}
    for (const variant of variants) {
      const key = variant.labels[index]
      if (!subGrouped[key]) subGrouped[key] = []
      subGrouped[key].push(variant)
    }

    const result: { [key: string]: any } = {}
    for (const key in subGrouped) {
      result[key] = groupVariants({
        headings,
        variants: subGrouped[key],
        index: index + 1,
      })
    }
    return result
  }
}
